import { Injectable } from "@angular/core";
import { GraphicsRepository } from "../../../domain/graphics-repository";
import { GraphicsInfo } from "../../../domain/graphics-info";
import { HttpService } from "../../http/http";
import { GraphicsMapper } from "./graphics.mapper";
import { UserService } from "../../../domain/user-service";
import { Subject } from "rxjs";
import { Logger } from "../../logger";
import moment from "moment";

@Injectable()
export class GraphicsRepositoryImpl implements GraphicsRepository {
  graphicUpdated = new Subject<GraphicsInfo>();
  templateDeleted = new Subject<{}>();

  templates = [];

  constructor(
    private httpService: HttpService,
    private userService: UserService,
    private graphicsMapper: GraphicsMapper,
    private logger: Logger,
  ) {}

  getTemplatesNamesAndIds(): Promise<{ id: number; name: string }> {
    return this.httpService
      .get("Graphic/getGraphicTemplatesNames")
      .then((response) =>
        response.map((x) => {
          return { id: x.Id, name: x.Name };
        }),
      );
  }

  getGraphicsSearch(
    searchQuery: string,
    orderByGraphics: number,
    sortByGraphics: string,
    startDateCreatedGraphics: moment.Moment,
    endDateCreatedGraphics: moment.Moment,
    templateIds: number[],
  ): Promise<GraphicsInfo[]> {
    const templatesUrl = templateIds.map((x) => `TemplateIds=${x}`).join("&");

    const url =
      `graphic?SearchQuery=${searchQuery}&OrderBy=${orderByGraphics}&SortBy=${sortByGraphics}` +
      `&${templatesUrl}&DateFrom=${
        startDateCreatedGraphics
          ? startDateCreatedGraphics.format("YYYYMMDD")
          : ""
      }` +
      `&DateTo=${
        endDateCreatedGraphics ? endDateCreatedGraphics.format("YYYYMMDD") : ""
      }`;

    return this.httpService.get(url).then((response) => {
      return this.graphicsMapper.mapGetAllGraphicsResponse(response);
    });
  }

  getAll(): Promise<GraphicsInfo[]> {
    let currentChannel;
    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        currentChannel = channel;
      })
      .then(() => {
        return this.httpService.get("Graphic");
      })
      .then(this.graphicsMapper.mapGetAllGraphicsResponse);
  }

  getGraphics(id: number): Promise<GraphicsInfo> {
    return this.httpService
      .get("Graphic/" + id)
      .then((result) =>
        this.graphicsMapper.mapGetGraphicInfoResponse(result, false),
      );
  }

  getGraphicsTemplate(id: number): Promise<GraphicsInfo> {
    return this.httpService
      .get("Graphic/Template/" + id)
      .then((result) =>
        this.graphicsMapper.mapGetGraphicInfoResponse(result, true),
      );
  }

  save(graphicsInfo: GraphicsInfo): Promise<void> {
    let currentChannel;

    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        currentChannel = channel;
      })
      .then(() => {
        return this.httpService.post(
          "Graphic",
          this.graphicsMapper.mapSaveGraphicInfoRequest(graphicsInfo),
        );
      })
      .then((instanceCreated) => {
        this.graphicsMapper
          .mapGetGraphicInfoResponse(instanceCreated, false)
          .then(
            (mappedInstance) => {
              mappedInstance.instanceTemplateId = graphicsInfo.id;
              this.graphicUpdated.next(mappedInstance);
            },
            (error) => {
              console.log("[ERROR]:", error);
            },
          )
          .catch((error) => {
            console.log("[EXCEPTION]:", error);
          });
      });
  }

  setGoLive(graphicsInfo: GraphicsInfo): Promise<void> {
    let currentChannel;
    return this.userService
      .getCurrentChannel()
      .then((channel) => {
        currentChannel = channel;
      })
      .then(() => {
        const goLiveRequest =
          this.graphicsMapper.mapSaveGraphicInfoRequest(graphicsInfo);
        goLiveRequest["ChannelId"] = currentChannel.id;
        return this.httpService.post("Graphic/GoLive", goLiveRequest);
      });
  }

  graphicsUpload(
    url: string,
    filename: string,
    graphicsName: string,
    graphicsDescription: string,
  ) {
    return this.httpService.post("Graphic/UploadTemplate", {
      contentUrl: url,
      userFileName: filename,
      graphicsName: graphicsName,
      graphicsDescription: graphicsDescription,
    });
  }

  thumbnailUpload(url: string, filename: string, graphicsId: number) {
    return this.httpService.post("Graphic/UploadThumbnail", {
      contentUrl: url,
      userFileName: filename,
      graphicsId: graphicsId,
    });
  }

  sampleVideoUpload(url: string, filename: string, graphicsId: number) {
    return this.httpService.post("Graphic/UploadSampleVideo", {
      contentUrl: url,
      userFileName: filename,
      graphicsId: graphicsId,
    });
  }

  fontUpload(url: string, fontName: string) {
    return this.httpService.post("Font", {
      contentUrl: url,
      fontName: fontName,
    });
  }

  deleteGraphics(templateId: number) {
    return this.httpService
      .delete(`Graphic/Template/${templateId}`)
      .then(() => {
        this.templateDeleted.next(true);
      });
  }

  updateRenditionValidityPeriod(
    graphicTemplateId: number,
    renditionValidityPeriodMs: number,
  ) {
    return this.httpService.post(`Graphic/UpdateRenditionValidityPeriod`, {
      GraphicTemplateId: graphicTemplateId,
      RenditionValidityPeriodMs: renditionValidityPeriodMs,
    });
  }

  /**
   * removing a graphic instance from server
   * @param instanceId
   * @return {any}
   */
  deleteInstance(instanceId: number) {
    return this.httpService.delete(`Graphic/${instanceId}`).then(() => {
      return this.templateDeleted.next(true);
    });
  }
}
